import { useContext } from 'react';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@ltvco/refresh-lib/vendors';
import { enqueueSnackbar, useServiceToken } from '@ltvco/refresh-lib/v1';
import { ResponseError } from '@ltvco/refresh-lib/utils';
import {
  Answer,
  CreateAnswerParams,
  CreateQuestionParams,
  GetQuestionsParams,
  GetQuestionsResponse,
  GetQuestionResponse,
} from 'api/requests/communityBuzz/interfaces';
import { communityBuzzRequests } from 'api/requests/communityBuzz/communityBuzz';

export interface CommunityBuzzParams {
  questionsListParams: GetQuestionsParams;
  questionId?: number;
}

export const useCommunityBuzz = ({
  questionsListParams,
  questionId,
}: CommunityBuzzParams) => {
  const { logError } = useContext(AppConfig);
  const { data: oramTokenData } = useServiceToken('oram');
  const queryClient = useQueryClient();
  const hasQuestionsParams =
    Boolean(questionsListParams?.year) &&
    Boolean(questionsListParams?.make) &&
    Boolean(questionsListParams?.model) &&
    Boolean(questionsListParams?.trim);

  const questionsListQuery = useQuery({
    queryKey: [
      'community-buzz',
      'questions',
      questionsListParams,
      oramTokenData?.token ?? 'public',
    ],
    retry: false,
    queryFn: ({ signal }) =>
      communityBuzzRequests.getQuestions(
        questionsListParams,
        oramTokenData?.token,
        undefined,
        undefined,
        signal,
      ),
    enabled: hasQuestionsParams,
    refetchOnMount: true,
    onError: (err: Error) => {
      enqueueSnackbar('Error loading community buzz questions.', {
        variant: 'error',
      });
      logError('Failed to fetch community buzz questions', err as Error);
    },
  });

  const questionQuery = useQuery({
    queryKey: [
      'community-buzz',
      'question',
      questionId,
      oramTokenData?.token ?? 'public',
    ],
    queryFn: () =>
      communityBuzzRequests.getQuestion(
        { id: questionId as number },
        oramTokenData?.token,
      ),
    enabled: Boolean(questionId),
  });

  const createQuestionMutation = useMutation({
    mutationFn: (params: CreateQuestionParams) => {
      if (!oramTokenData?.token) {
        throw new Error('Missing service token for community buzz question.');
      }

      return communityBuzzRequests.createQuestion(params, oramTokenData.token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['community-buzz', 'questions', questionsListParams],
      });
    },
    onError: (err: Error) => {
      // If the content moderation failed, we don't want to show an error message
      // because the user will be notified through the QAEntryModal
      if ((err as ResponseError).response?.status !== 422) {
        enqueueSnackbar(
          'Error submitting a community buzz question. Please try again.',
          {
            variant: 'error',
          },
        );
        logError('Failed to submit a community buzz question', err as Error);
      }
    },
  });

  const createAnswerMutation = useMutation({
    mutationFn: (params: CreateAnswerParams) => {
      if (!oramTokenData?.token) {
        throw new Error('Missing service token for community buzz answer.');
      }

      return communityBuzzRequests.createAnswer(params, oramTokenData.token);
    },
    onSuccess: (data, variables) => {
      const tokenKey = oramTokenData?.token ?? 'public';
      const newAnswer =
        data && typeof data === 'object' && 'item' in data
          ? (data as { item?: Answer }).item
          : undefined;

      queryClient.setQueryData(
        ['community-buzz', 'questions', questionsListParams, tokenKey],
        (previous: GetQuestionsResponse | undefined) => {
          if (!previous) {
            return previous;
          }

          const updatedItems = previous.items.map((question) => {
            if (question.id !== variables.question_id) {
              return question;
            }

            const updated = { ...question };
            if (typeof updated.answers_count === 'number') {
              updated.answers_count += 1;
            } else {
              updated.answers_count = 1;
            }

            return updated;
          });

          return {
            ...previous,
            items: updatedItems,
          };
        },
      );

      queryClient.setQueryData(
        ['community-buzz', 'question', variables.question_id, tokenKey],
        (previous: GetQuestionResponse | undefined) => {
          if (!previous?.item) {
            return previous;
          }

          const existingAnswers = Array.isArray(previous.item.answers)
            ? previous.item.answers
            : [];

          const updatedAnswers = newAnswer
            ? [...existingAnswers, newAnswer]
            : existingAnswers;

          return {
            ...previous,
            item: {
              ...previous.item,
              answers: updatedAnswers,
              answers_count:
                typeof previous.item.answers_count === 'number'
                  ? previous.item.answers_count + 1
                  : updatedAnswers.length,
            },
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: ['community-buzz', 'question', variables.question_id],
      });
    },
    onError: (err: Error) => {
      // If the content moderation failed, we don't want to show an error message
      // because the user will be notified through the QAEntryModal
      if ((err as ResponseError).response?.status !== 422) {
        enqueueSnackbar(
          'Error submitting a community buzz answer. Please try again.',
          {
            variant: 'error',
          },
        );
        logError('Failed to submit a community buzz answer', err as Error);
      }
    },
  });

  return {
    questionsListQuery,
    questionQuery,
    createQuestionMutation,
    createAnswerMutation,
    bootstrapTokenReady: Boolean(oramTokenData?.token),
  };
};
